import { useState } from 'react';
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Text,
} from '@fluentui/react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDestructive?: boolean;
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isDestructive = true,
}: ConfirmDialogProps) {
  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onCancel}
      dialogContentProps={{
        type: DialogType.normal,
        title,
      }}
      modalProps={{
        isBlocking: true,
      }}
    >
      <Text>{message}</Text>
      <DialogFooter>
        <PrimaryButton
          text={confirmText}
          onClick={onConfirm}
          styles={
            isDestructive
              ? {
                  root: { backgroundColor: '#a4262c', borderColor: '#a4262c' },
                  rootHovered: { backgroundColor: '#8c1d23', borderColor: '#8c1d23' },
                  rootPressed: { backgroundColor: '#751a1e', borderColor: '#751a1e' },
                }
              : undefined
          }
        />
        <DefaultButton text={cancelText} onClick={onCancel} />
      </DialogFooter>
    </Dialog>
  );
}

// Hook for using confirm dialog
// eslint-disable-next-line react-refresh/only-export-components
export function useConfirmDialog() {
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const showConfirm = (title: string, message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({
        isOpen: true,
        title,
        message,
        onConfirm: () => {
          setDialogState((prev) => ({ ...prev, isOpen: false }));
          resolve(true);
        },
      });
    });
  };

  const hideConfirm = () => {
    setDialogState((prev) => ({ ...prev, isOpen: false }));
  };

  const ConfirmDialogComponent = () => (
    <ConfirmDialog
      isOpen={dialogState.isOpen}
      title={dialogState.title}
      message={dialogState.message}
      onConfirm={dialogState.onConfirm}
      onCancel={hideConfirm}
    />
  );

  return { showConfirm, hideConfirm, ConfirmDialogComponent, dialogState };
}
